import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { DataRoomUser, DataRoom, UnlockUser, MilestoneReport } from '../../../shared/models';
import { EntityUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';

interface LenderParticipantActionButtonProps {
	isEmployee: boolean;
	isBorrower: boolean;
	isBanker: boolean;
	user: DataRoomUser;
	dataRoom: DataRoom;
	milestone: MilestoneReport | null;
	unlockUser: (user: UnlockUser) => void;
	accessLogs: (user: UnlockUser) => void;
	optOutUser: (user: DataRoomUser) => void;
	addNote: (user: DataRoomUser) => void;
	viewNotes: (user: DataRoomUser) => void;
	addTeamMember: (user: DataRoomUser) => void;
	sendInvite: (user: UnlockUser) => void;
	sendEmail: (user: DataRoomUser) => void;
	viewEmails: (user: DataRoomUser) => void;
	uploadNda: (user: DataRoomUser) => void;
	grantAccess: (user: DataRoomUser) => void;
	deleteDataRoomUser: (user: DataRoomUser) => void;
	contactDetails: (user: DataRoomUser) => void;
	progressDetails: (milestone: MilestoneReport | null) => void;
}

type LenderParticipantActionItem = keyof typeof LenderParticipantActionItemMap;
const LenderParticipantActionItemMap = {
	OptOut: 'Opt-Out',
	Unlock: 'Unlock Provider',
	AddNote: 'Add Note',
	Notes: 'View All Notes',
	AccessLogs: 'View Access Logs',
	AddTeamMember: 'Add Team Member',
	SendInvite: 'Send Invite',
	SendEmail: 'Send Email',
	ViewEmails: 'View Sent Emails',
	UploadNda: 'Upload NDA',
	GrantAccess: 'Grant Access',
	DeleteDataRoomUser: 'Delete',
	ContactDetails: 'Contact Details',
	ProgressDetails: 'Progress Details',
	DownloadSignedNda: 'Download Signed NDA'
};

export const LenderParticipantActionButton: React.FC<LenderParticipantActionButtonProps> = ({
	isEmployee,
	isBorrower,
	isBanker,
	user,
	dataRoom,
	milestone,
	unlockUser,
	accessLogs,
	optOutUser,
	addNote,
	viewNotes,
	addTeamMember,
	sendInvite,
	sendEmail,
	viewEmails,
	uploadNda,
	grantAccess,
	deleteDataRoomUser,
	contactDetails,
	progressDetails
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isRoomOpen = dataRoom.roomStatus == 'Open';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: LenderParticipantActionItem) => {
		const userDto = EntityUtils.GetUnlockUserFromDataRoomUser(user);

		switch (action) {
			case 'Unlock':
				unlockUser(userDto ?? ({} as UnlockUser));
				break;
			case 'OptOut':
				optOutUser(user);
				break;
			case 'AddNote':
				addNote(user);
				break;
			case 'Notes':
				viewNotes(user);
				break;
			case 'AccessLogs':
				accessLogs(userDto ?? ({} as UnlockUser));
				break;
			case 'AddTeamMember':
				addTeamMember(user);
				break;
			case 'SendInvite':
				sendInvite(userDto ?? ({} as UnlockUser));
				break;
			case 'ViewEmails':
				viewEmails(user);
				break;
			case 'SendEmail':
				sendEmail(user);
				break;
			case 'UploadNda':
				uploadNda(user);
				break;
			case 'GrantAccess':
				grantAccess(user);
				break;
			case 'DeleteDataRoomUser':
				deleteDataRoomUser(user);
				break;
			case 'ContactDetails':
				contactDetails(user);
				break;
			case 'ProgressDetails':
				progressDetails(milestone);
				break;
			case 'DownloadSignedNda': {
				const url = CerebroApiEndpoints.LenderNda.Download(dataRoom.guid, user.userGuid);
				window.open(url, '_blank');
				break;
			}
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: LenderParticipantActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									{milestone != null && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ProgressDetails')}>
											{LenderParticipantActionItemMap['ProgressDetails']}
										</MenuItem>
									)}
									{user.dealBankStatus == 'inactive' && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'GrantAccess')}>
											{LenderParticipantActionItemMap['GrantAccess']}
										</MenuItem>
									)}
									{isEmployee && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ContactDetails')}>
											{LenderParticipantActionItemMap['ContactDetails']}
										</MenuItem>
									)}
									{isEmployee &&
										isRoomOpen &&
										!user.isLockedOut &&
										user.dealBankStatus == 'active' && (
											<MenuItem onClick={event => handleMenuItemClick(event, 'SendInvite')}>
												{LenderParticipantActionItemMap['SendInvite']}
											</MenuItem>
										)}
									{isRoomOpen && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'SendEmail')}>
											{LenderParticipantActionItemMap['SendEmail']}
										</MenuItem>
									)}
									{isEmployee && user.dealBankStatus == 'active' && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AddTeamMember')}>
											{LenderParticipantActionItemMap['AddTeamMember']}
										</MenuItem>
									)}
									{isEmployee &&
										user.allowAccess &&
										user.dealBankStatus == 'active' &&
										!user.optedOut && (
											<MenuItem onClick={event => handleMenuItemClick(event, 'OptOut')}>
												{LenderParticipantActionItemMap['OptOut']}
											</MenuItem>
										)}
									{!isBanker && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AddNote')}>
											{LenderParticipantActionItemMap['AddNote']}
										</MenuItem>
									)}
									{isRoomOpen && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ViewEmails')}>
											{LenderParticipantActionItemMap['ViewEmails']}
										</MenuItem>
									)}
									{isEmployee && user.noteCount > 0 && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Notes')}>
											{LenderParticipantActionItemMap['Notes']}
										</MenuItem>
									)}
									{isEmployee && user.dealBankStatus == 'active' && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'UploadNda')}>
											{LenderParticipantActionItemMap['UploadNda']}
										</MenuItem>
									)}
									{!isBanker && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AccessLogs')}>
											{LenderParticipantActionItemMap['AccessLogs']}
										</MenuItem>
									)}
									{isEmployee && user.isLockedOut && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Unlock')}>
											{LenderParticipantActionItemMap['Unlock']}
										</MenuItem>
									)}
									{isEmployee && user.dealBankStatus == 'inactive' && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'DeleteDataRoomUser')}>
											{LenderParticipantActionItemMap['DeleteDataRoomUser']}
										</MenuItem>
									)}
									{isEmployee && (user.accessed || user.signedNda) && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'DownloadSignedNda')}>
											{LenderParticipantActionItemMap['DownloadSignedNda']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
